import { useState } from "react";
import { Table, Tag, Input, Card, Tooltip, Badge, Collapse } from "antd";
import { Search, GitBranch, AlertCircle } from "lucide-react";
import { usePipelineOverview } from "../../../api-hooks/companies";
import dayjs from "dayjs";

const { Panel } = Collapse;

export default function PlatformPipelinePage() {
  const [search, setSearch] = useState("");
  const { data: companies = [], isLoading } = usePipelineOverview();

  const filtered = companies.filter((c) => {
    if (!search) return true;
    return c.company_name?.toLowerCase().includes(search.toLowerCase());
  });

  const withPipelines = companies.filter((c) => c.pipeline_count > 0).length;
  const noPipelines = companies.filter((c) => c.pipeline_count === 0).length;

  const columns = [
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      render: (v: string) => <span className="font-medium text-slate-800">{v}</span>,
    },
    {
      title: "Pipelines",
      dataIndex: "pipeline_count",
      key: "pipeline_count",
      sorter: (a: any, b: any) => a.pipeline_count - b.pipeline_count,
      render: (v: number) =>
        v === 0 ? (
          <Tag color="default">None configured</Tag>
        ) : (
          <Badge count={v} style={{ backgroundColor: "#4c1d95" }} />
        ),
    },
    {
      title: "Total Stages",
      dataIndex: "stage_count",
      key: "stage_count",
      sorter: (a: any, b: any) => a.stage_count - b.stage_count,
      render: (v: number) =>
        v === 0 ? (
          <span className="text-slate-400">—</span>
        ) : (
          <span className="text-sm font-semibold text-violet-700">{v}</span>
        ),
    },
    {
      title: "Last Updated",
      dataIndex: "last_updated",
      key: "last_updated",
      render: (v: string | null) =>
        v ? (
          <Tooltip title={dayjs(v).format("DD MMM YYYY HH:mm")}>
            <span className="text-sm text-slate-500">{dayjs(v).fromNow?.() ?? dayjs(v).format("DD MMM YYYY")}</span>
          </Tooltip>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      title: "Pipeline Detail",
      key: "detail",
      render: (_: any, row: any) => {
        if (!row.pipelines || row.pipelines.length === 0) {
          return <span className="text-slate-400 text-xs italic">No pipelines</span>;
        }
        return (
          <Collapse ghost size="small" className="border-0">
            {row.pipelines.map((p: any) => (
              <Panel
                key={p.pipeline_id}
                header={
                  <span className="text-xs font-medium text-slate-700">
                    {p.pipeline_name}{" "}
                    <span className="text-slate-400">({p.stages?.length ?? 0} stages)</span>
                  </span>
                }
              >
                <div className="flex flex-wrap gap-1 mt-1">
                  {(p.stages ?? [])
                    .sort((a: any, b: any) => a.order - b.order)
                    .map((s: any, i: number) => (
                      <Tag key={i} className="text-xs" color="geekblue">
                        {i + 1}. {s.name}
                      </Tag>
                    ))}
                </div>
              </Panel>
            ))}
          </Collapse>
        );
      },
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <GitBranch size={20} className="text-violet-700" />
            <h2 className="text-xl font-semibold text-slate-800">Pipeline Configuration Overview</h2>
          </div>
          <p className="text-slate-500 text-sm">
            Job pipeline stages configured across all companies
          </p>
        </div>
        <div className="flex gap-3">
          {withPipelines > 0 && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl px-3 py-2">
              <span className="text-violet-700 font-semibold text-sm">{withPipelines} With Pipelines</span>
            </div>
          )}
          {noPipelines > 0 && (
            <Tooltip title="Companies with no pipelines configured">
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                <AlertCircle size={14} className="text-slate-400" />
                <span className="text-slate-600 font-semibold text-sm">{noPipelines} Not Configured</span>
              </div>
            </Tooltip>
          )}
        </div>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm mb-4">
        <Input
          prefix={<Search size={14} className="text-slate-400" />}
          placeholder="Search by company name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg"
          style={{ maxWidth: 360 }}
          allowClear
        />
      </Card>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="company_id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 30, showTotal: (t) => `${t} companies` }}
          scroll={{ x: "max-content" }}
          rowClassName={(r) => (r.pipeline_count === 0 ? "opacity-60" : "")}
        />
      </Card>
    </div>
  );
}
